import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  InputAdornment,
  Typography,
  Snackbar,
  Alert,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import MessageIcon from '@mui/icons-material/Message';

const ContactDialog = ({ open, handleClose }) => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    message: '',
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success', // 'success' | 'error'
  });

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

const handleSubmit = async (e) => {
  e.preventDefault();

  const { name, email, message } = form;

  // Basic email regex pattern
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Validate fields
  if (!name || !email || !message) {
    setSnackbar({
      open: true,
      message: 'All fields are required.',
      severity: 'error',
    });
    return;
  }

  if (!emailRegex.test(email)) {
    setSnackbar({
      open: true,
      message: 'Please enter a valid email address.',
      severity: 'error',
    });
    return;
  }

  // Proceed to send form
  try {
    const response = await fetch('https://formspree.io/f/mjkoalye', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(form),
    });

    if (response.ok) {
      setSnackbar({
        open: true,
        message: 'Message sent successfully!',
        severity: 'success',
      });
      setForm({ name: '', email: '', message: '' });
      handleClose();
    } else {
      setSnackbar({
        open: true,
        message: 'Failed to send message. Please try again.',
        severity: 'error',
      });
    }
  } catch (error) {
    setSnackbar({
      open: true,
      message: 'An error occurred. Please try again later.',
      severity: 'error',
    });
  }
};


  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 3,
            boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold" textAlign="center">
            Contact Us
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              mt: 1,
            }}
          >
            <TextField
              fullWidth
              label="Your Name"
              name="name"
              value={form.name}
              onChange={handleChange}
              required
              placeholder="Enter your full name"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Your Email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              required
              placeholder="name@example.com"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Message"
              name="message"
              multiline
              rows={5}
              value={form.message}
              onChange={handleChange}
              required
              placeholder="Write your message here..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MessageIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <DialogActions sx={{ px: 0 }}>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                sx={{
                  background: 'linear-gradient(90deg, #22c946ff 0%, #a3e635 100%)',
                  color: 'black',
                  fontWeight: 'bold',
                  borderRadius: 2,
                  textTransform: 'none',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #1a9b38 0%, #7cb72f 100%)',
                  },
                }}
              >
                Send Message
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Snackbar Notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ContactDialog;
